/**
 * Financial year utilities for IOD PARC.
 *
 * The financial year runs 1 May – 30 April. A "financial year start" is the
 * calendar year in which that May–April cycle begins (e.g. 2025 => May 2025 – Apr 2026).
 *
 * Shared by the PPD Admin and Events Admin panels so budget bucketing stays consistent.
 */

/** Minimal shape needed to bucket a record into a financial year. */
export interface FinancialYearBucketable {
    status: string;
    approval_date?: string;
    submission_date?: string;
    created_at?: string;
    financial_year_start?: number | null;
}

/**
 * Returns the FY start (calendar) year for a given date, or null if the date is missing/invalid.
 */
export function getFinancialYear(dateStr?: string | null): number | null {
    if (!dateStr) {
        return null;
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
        return null;
    }
    // Month index >= 4 (May) belongs to that calendar year, else the previous year
    return date.getMonth() >= 4 ? date.getFullYear() : date.getFullYear() - 1;
}

/**
 * Human-readable label for a financial year start, e.g. "May 2025 – Apr 2026".
 */
export function formatFYLabel(startYear: number): string {
    return `May ${startYear} – Apr ${startYear + 1}`;
}

/**
 * The date used to bucket a record into a financial year:
 * approval date for approved records, otherwise submission/created date.
 */
export function getFormFYDate(form: FinancialYearBucketable): string | undefined {
    if (form.status === 'approved' && form.approval_date) {
        return form.approval_date;
    }
    return form.submission_date || form.created_at;
}

/**
 * Admin assignment (financial_year_start) takes precedence over the date-derived financial year.
 */
export function getFormFinancialYear(form: FinancialYearBucketable): number | null {
    return form.financial_year_start ?? getFinancialYear(getFormFYDate(form));
}

/**
 * Current financial year start based on today.
 */
export function getCurrentFinancialYear(): number {
    const fy = getFinancialYear(new Date().toISOString());
    if (fy === null) {
        // Should never happen for a valid system clock, but fail loudly if it does.
        throw new Error('Failed to calculate current financial year');
    }
    return fy;
}

/**
 * Format a number as GBP currency (no pence unless present).
 */
export function formatGBP(value: number): string {
    return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/**
 * Financial years to offer in an editable FY dropdown:
 * next FY, current, and the previous two, plus any already present in the data.
 */
export function buildFinancialYearOptions(dataYears: (number | null)[]): number[] {
    const current = getCurrentFinancialYear();
    return Array.from(
        new Set<number>([
            current + 1,
            current,
            current - 1,
            current - 2,
            ...dataYears.filter((fy): fy is number => fy != null)
        ])
    ).sort((a, b) => b - a);
}
